import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { parseServerDate } from '../../utils/serverDate';
import './Admin.css';

const fmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [encryption, setEncryption] = useState(null);

  useEffect(() => {
    let cancelled = false;
    adminAPI.getStats()
      .then((res) => { if (!cancelled) setStats(res.data?.stats || {}); })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || err.message || 'Failed to load stats');
      });
    // Optional: if this call fails the dashboard simply shows no encryption banner
    adminAPI.getSettingsEncryption()
      .then((res) => { if (!cancelled) setEncryption(res.data?.success ? res.data : null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const encRows = encryption?.rows || {};
  const unreadableCount = (Number(encRows.unknown_key) || 0) + (Number(encRows.tampered) || 0);
  const plaintextCount = Number(encRows.plaintext_secret) || 0;

  const byPlan = stats?.active_by_plan || {};
  const cards = stats ? [
    { label: 'Customers', value: fmt(stats.customers) },
    { label: 'Active subscriptions', value: fmt(stats.active_subscriptions) },
    { label: 'Estimated MRR', value: stats.estimated_mrr === undefined ? '—' : `$${Number(stats.estimated_mrr).toLocaleString()}` },
    { label: 'New this month', value: fmt(stats.new_this_month) },
    { label: 'Credits granted (30d)', value: fmt(stats.credits_granted_30d) },
    { label: 'Credits spent (30d)', value: fmt(stats.credits_spent_30d) },
    { label: 'Searches (30d)', value: fmt(stats.searches_30d) },
  ] : [];

  return (
    <div className="admin-page">
      <h1>Dashboard</h1>
      {error && <div className="admin-error">{error}</div>}
      {encryption && encryption.configured === false && (
        <div className="admin-error">
          Saved customer keys cannot be encrypted or read: SETTINGS_ENCRYPTION_KEY is not set on the backend.
        </div>
      )}
      {encryption && encryption.configured !== false && unreadableCount > 0 && (
        <div className="admin-error">
          {unreadableCount} saved customer {unreadableCount === 1 ? 'key' : 'keys'} can't be read (wrong or removed encryption key, or edited in the database).
        </div>
      )}
      {encryption && encryption.configured !== false && plaintextCount > 0 && (
        <div className="admin-error">
          {plaintextCount} saved customer {plaintextCount === 1 ? 'key is' : 'keys are'} still unencrypted: run scripts/encrypt_settings.py --apply.
        </div>
      )}
      {encryption && encryption.configured && encryption.primary_kid && (
        <small>Settings encryption key id: {encryption.primary_kid}</small>
      )}
      {!stats && !error && <p>Loading…</p>}

      {stats && (
        <>
          <div className="stats-grid">
            {cards.map((c, i) => (
              <div className={`stat-card admin-stat admin-stat-${i % 4}`} key={c.label}>
                <div className="stat-value">{c.value}</div>
                <div className="stat-label">{c.label}</div>
              </div>
            ))}
          </div>

          <div className="card admin-card" style={{ marginTop: 16 }}>
            <h2 className="card-title">Active subscriptions by plan</h2>
            {Object.keys(byPlan).length === 0 ? (
              <p>No active subscriptions.</p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {Object.entries(byPlan).map(([plan, count]) => (
                  <li key={plan}>{plan}: {fmt(count)}</li>
                ))}
              </ul>
            )}
          </div>

          <div className="card admin-card" style={{ marginTop: 16 }}>
            <h2 className="card-title">Recent signups</h2>
            {(stats.recent_signups || []).length === 0 ? (
              <p>No signups yet.</p>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr><th>Email</th><th>Name</th><th>Signed up</th></tr>
                </thead>
                <tbody>
                  {stats.recent_signups.map((u) => (
                    <tr key={u.id}>
                      <td>{u.email}</td>
                      <td>{u.name || '—'}</td>
                      <td>{parseServerDate(u.created_at)?.toLocaleDateString() || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p style={{ marginTop: 8 }}><Link to="/admin/customers">All customers</Link></p>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminDashboard;
